import React, {ReactNode, useRef, useState} from 'react';
import {Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import {EstimateGateDetailsDto} from '../../../../shared/dtos/estimate-gate.dto';
import {EstimateTreeDto} from '../../../../shared/dtos/estimate-tree.dto';
import {PhotoEdit} from '../../../../shared/services/image-service';
import {PhotoViewerWithEditor} from '../../../../shared/components/PhotoViewerWithEditor';
import {COLORS, GateGlyph, craneArt, truckChipperArt, workItemLabel} from './map-art';
import {GATE_WIDTH_OPTIONS, formatGateWidth, gateWidthInches, gateWidthLabel} from './gate-width';
import {CRANE_ART_ASPECT} from './map-geometry';
import {hintLines} from './map-hint';
import type {SiteMap} from './useSiteMap';

/*
 * What sits over the job site map (SQ-621), each Android's own: the Truck &
 * Chipper panel (ChipperTruckPanel.kt, also the crane's), the gate's panel
 * (GatePanel.kt), the "Getting around the map" card and the + menu.
 */

// ---- Truck & Chipper and Crane ----------------------------------------------------------------------

export interface PanelChip {
  treeId: number;
  /** "3 Tree": the work item's number and type. */
  label: string;
  checked: boolean;
  /** Its route is the one being shaped. */
  live: boolean;
}

export const chipLabel = (tree: EstimateTreeDto, number: number | undefined) => `${number ?? ''} ${workItemLabel(tree.workItemType)}`;

export const MachinePanel = ({number, title, chips, emptyMessage, canEdit, badgeColor, badgeText, deleteLabel, showLive, onToggleConnect, onToggleLive, onDelete, onClose}: {
  number: number;
  title: string;
  chips: PanelChip[];
  emptyMessage: string;
  canEdit: boolean;
  badgeColor: string;
  badgeText: string;
  deleteLabel: string;
  /** A crane's reach cannot be shaped: its names are just names. */
  showLive: boolean;
  onToggleConnect: (chip: PanelChip) => void;
  onToggleLive: (chip: PanelChip) => void;
  onDelete: () => void;
  onClose: () => void;
}) => (
  <section className="sm-panel" aria-label={title}>
    <div className="sm-panel-head">
      <span className="sm-badge" style={{background: badgeColor, color: badgeText}}>{number}</span>
      <span className="sm-panel-title">{title}</span>
      {canEdit && (
        <button type="button" className="sm-icon-button sm-icon-button--danger" onClick={onDelete} aria-label={deleteLabel}><DeleteIcon/></button>
      )}
      <button type="button" className="sm-text-button" onClick={onClose}>Close</button>
    </div>
    <div className="sm-rule"/>
    {chips.length === 0
      ? <div className="sm-empty">{emptyMessage}</div>
      : (
        <div className="sm-chips">
          {chips.map(chip => {
            const settled = canEdit && chip.checked && !chip.live;
            return (
              <div key={chip.treeId} className={chip.checked ? 'sm-chip sm-chip--checked' : 'sm-chip'}>
                <Checkbox size="small" checked={chip.checked} disabled={!canEdit} onChange={() => onToggleConnect(chip)}
                          inputProps={{'aria-label': `Connect ${chip.label}`}} sx={{p: '6px'}}/>
                {showLive
                  ? (
                    <button type="button" disabled={!canEdit || !chip.checked} onClick={() => onToggleLive(chip)}
                            className={chip.live ? 'sm-chip-name sm-chip-name--live' : settled ? 'sm-chip-name sm-chip-name--settled' : 'sm-chip-name'}
                            aria-pressed={chip.live}>
                      {chip.label}
                    </button>
                  )
                  : <span className="sm-chip-label">{chip.label}</span>}
              </div>
            );
          })}
        </div>
      )}
  </section>
);

// ---- Gates -------------------------------------------------------------------------------------------------

export interface GatePanelActions {
  onSetWidth: (inches: number) => void;
  onToggleWorkItem: (treeId: number) => void;
  pathThrough: (treeId: number) => boolean;
  onAddPhotos: (files: File[]) => void;
  onReplacePhoto: (imageId: number, edit: PhotoEdit) => Promise<void>;
  onDeletePhoto: (imageId: number) => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * One gate's panel actions, the same on the map and on the record (Android's
 * MapWorkspaceController.gatePanelActions). `onClosed` runs on Close and after
 * a delete, which also closes the panel.
 */
export const gatePanelActions = (siteMap: SiteMap, gateId: number, onClosed: () => void): GatePanelActions => ({
  onSetWidth: inches => void siteMap.setGateWidth(gateId, inches),
  onToggleWorkItem: treeId => siteMap.toggleGateWorkItem(gateId, treeId),
  pathThrough: treeId => siteMap.pathThroughGate(gateId, treeId),
  onAddPhotos: files => void siteMap.uploadGatePhotos(gateId, files),
  onReplacePhoto: (imageId, edit) => siteMap.replaceGatePhoto(gateId, imageId, edit),
  onDeletePhoto: imageId => void siteMap.deleteGatePhoto(gateId, imageId),
  onDelete: () => {
    void siteMap.deleteGate(gateId);
    onClosed();
  },
  onClose: onClosed,
});

const SimpleDialog = ({title, text, actions, onClose}: {title: string; text: string; actions: React.ReactNode; onClose: () => void}) => (
  <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{text}</DialogContent>
    <DialogActions>{actions}</DialogActions>
  </Dialog>
);

export const GatePanel = ({gate, number, uploads, trees, treeNumbers, canEdit, actions, footer, className}: {
  gate: EstimateGateDetailsDto;
  number: number;
  /** Pictures still uploading, as local previews. */
  uploads: string[];
  trees: EstimateTreeDto[];
  treeNumbers: Map<number, number>;
  canEdit: boolean;
  actions: GatePanelActions;
  /** Below the chips: the record's "has been added to the map" note. */
  footer?: ReactNode;
  className?: string;
}) => {
  const [askSource, setAskSource] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [locked, setLocked] = useState<string>();
  const [viewing, setViewing] = useState<number>();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const label = `Gate ${number}`;
  const connected = new Set(gate.associatedTrees.map(t => t.id));
  const takeFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) actions.onAddPhotos(files);
  };
  const sorted = [...trees].sort((a, b) => (treeNumbers.get(a.id) ?? 0) - (treeNumbers.get(b.id) ?? 0));
  return (
    <section className={className ? `sm-panel sm-panel--gate ${className}` : 'sm-panel sm-panel--gate'} aria-label={`${label} panel`}>
      <div className="sm-panel-head">
        <span className="sm-panel-title sm-panel-title--bold">{label}</span>
        {/* The photos fill in from the camera towards the title. */}
        <div className="sm-thumbs">
          {gate.images.map((image, index) => (
            <button key={image.id} type="button" className="sm-thumb" onClick={() => setViewing(index)} aria-label={`${label} picture`}>
              <img src={image.imageUrl} alt=""/>
              <span className="sm-thumb-badge">{label}</span>
            </button>
          ))}
          {uploads.map(url => (
            <span key={url} className="sm-thumb sm-thumb--uploading">
              <img src={url} alt=""/>
              <span className="sm-thumb-badge">{label}</span>
              <span className="sm-thumb-spinner"><CircularProgress size={18} thickness={5} sx={{color: '#fff'}}/></span>
            </span>
          ))}
        </div>
        {canEdit && <>
          <button type="button" className="sm-camera" onClick={() => setAskSource(true)} aria-label="Take gate picture">
            <PhotoCameraIcon/>
            <span>Take picture</span>
          </button>
          <button type="button" className="sm-icon-button sm-icon-button--danger" onClick={() => setConfirmDelete(true)} aria-label="Delete gate"><DeleteIcon/></button>
        </>}
        <button type="button" className="sm-text-button" onClick={actions.onClose}>Close</button>
      </div>
      <div className="sm-rule"/>
      <div className="sm-gate-band">
        <div className="sm-gate-associate">
          <div>Associate with work items</div>
          <div className="sm-gate-associate-hint">(trees, stumps, etc.)</div>
        </div>
        <label className="sm-gate-width">
          <span>Gate width</span>
          {canEdit
            ? (
              <select value={gateWidthInches(gate.width) ? formatGateWidth(gateWidthInches(gate.width)!) : ''} aria-label="Gate width"
                      onChange={e => {
                        const index = GATE_WIDTH_OPTIONS.indexOf(e.target.value);
                        actions.onSetWidth(index < 0 ? 0 : index + 1);
                      }}>
                <option value="">{'—'}</option>
                {GATE_WIDTH_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            )
            : <strong className={gateWidthLabel(gate.width) ? undefined : 'sm-muted'}>{gateWidthLabel(gate.width) || '—'}</strong>}
        </label>
      </div>
      <div className="sm-rule sm-rule--soft"/>
      <div className="sm-chips">
        {trees.length === 0
          ? <div className="sm-empty">No work items yet — add them on the estimate.</div>
          : sorted.map(tree => {
            const text = chipLabel(tree, treeNumbers.get(tree.id));
            const checked = connected.has(tree.id);
            const toggle = () => {
              if (checked && actions.pathThrough(tree.id)) setLocked(text.trim());
              else actions.onToggleWorkItem(tree.id);
            };
            return (
              <label key={tree.id} className={checked ? 'sm-chip sm-chip--gate sm-chip--gate-checked' : 'sm-chip sm-chip--gate'}>
                <Checkbox size="small" checked={checked} disabled={!canEdit} onChange={toggle} inputProps={{'aria-label': `Associate ${text}`}} sx={{p: '5px'}}/>
                <span className="sm-chip-label">{text}</span>
              </label>
            );
          })}
      </div>
      {footer}

      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={takeFiles}/>
      <input ref={gallery} type="file" accept="image/*" multiple hidden onChange={takeFiles}/>
      {askSource && (
        <SimpleDialog title="Add a gate picture" text="Take a new photo or choose one from your gallery." onClose={() => setAskSource(false)}
                      actions={<>
                        <Button onClick={() => { setAskSource(false); gallery.current?.click(); }}>Gallery</Button>
                        <Button onClick={() => { setAskSource(false); camera.current?.click(); }}>Camera</Button>
                      </>}/>
      )}
      {locked && (
        <SimpleDialog title={`Can't disconnect ${locked}`} onClose={() => setLocked(undefined)}
                      text={`${locked}'s drag path goes through ${label}, so it stays connected. To disconnect it, move the drag path off the gate on the map.`}
                      actions={<Button onClick={() => setLocked(undefined)}>OK</Button>}/>
      )}
      {confirmDelete && (
        <SimpleDialog title={`Delete ${label}?`} text="The gate, its pictures and its work item connections are removed." onClose={() => setConfirmDelete(false)}
                      actions={<>
                        <Button onClick={() => setConfirmDelete(false)}>Keep</Button>
                        <Button color="error" onClick={() => { setConfirmDelete(false); actions.onDelete(); }}>Delete</Button>
                      </>}/>
      )}
      {viewing !== undefined && (
        // The shared viewer (SQ-657), as Android's GatePanel opens FullScreenPhotoViewer; Edit Picture and delete only while editable.
        <PhotoViewerWithEditor photos={gate.images.map(i => ({id: i.id, url: i.imageUrl, imageId: i.id}))}
                               startIndex={Math.min(viewing, Math.max(0, gate.images.length - 1))} onClose={() => setViewing(undefined)}
                               label={label} badge={label}
                               onSave={canEdit ? (p, edit) => actions.onReplacePhoto(p.id, edit) : undefined}
                               onDelete={canEdit ? p => actions.onDeletePhoto(p.id) : undefined}
                               deleteText="This removes the picture from the gate and from every work item it is connected to."/>
      )}
    </section>
  );
};

// ---- The hint card -------------------------------------------------------------------------------------------

export const HintCard = ({canEdit, mouse, onDismiss}: {canEdit: boolean; mouse: boolean; onDismiss: () => void}) => (
  <section className="sm-hint" aria-label="Getting around the map">
    <div className="sm-hint-text">
      <div className="sm-hint-title">Getting around the map</div>
      {hintLines(canEdit, mouse).map(line => <div key={line}>{line}</div>)}
    </div>
    <button type="button" className="sm-icon-button" onClick={onDismiss} aria-label="Dismiss instructions"><CloseIcon/></button>
  </section>
);

// ---- The + menu ---------------------------------------------------------------------------------------------------

const MenuRow = ({label, onClick, wide, children}: {label: string; onClick: () => void; wide?: boolean; children: React.ReactNode}) => (
  <div className="sm-add-row">
    <button type="button" className="sm-add-label" onClick={onClick}>{label}</button>
    <button type="button" className={wide ? 'sm-add-art sm-add-art--wide' : 'sm-add-art'} onClick={onClick} aria-label={`Add ${label}`}>{children}</button>
  </div>
);

/** Bottom right in edit mode: opens upward to Truck & Chipper, Crane and Gate. */
export const AddMenu = ({onAddTruck, onAddCrane, onAddGate}: {onAddTruck: () => void; onAddCrane: () => void; onAddGate: () => void}) => {
  const [open, setOpen] = useState(false);
  const pick = (add: () => void) => () => { setOpen(false); add(); };
  return (
    <div className="sm-add">
      {open && (
        <div className="sm-add-list">
          <MenuRow label="Truck & Chipper" onClick={pick(onAddTruck)}><img src={truckChipperArt} alt="" style={{width: 20, height: 56}}/></MenuRow>
          <MenuRow label="Crane" onClick={pick(onAddCrane)}>
            <span className="sm-crane-upright" style={{width: 56 * CRANE_ART_ASPECT, height: 56}}>
              <img src={craneArt} alt="" style={{width: 56, height: 56 * CRANE_ART_ASPECT}}/>
            </span>
          </MenuRow>
          <MenuRow label="Gate" onClick={pick(onAddGate)} wide><GateGlyph width={40}/></MenuRow>
        </div>
      )}
      <button type="button" className="sm-fab" onClick={() => setOpen(o => !o)} aria-label={open ? 'Close add menu' : 'Add to the map'}>
        {open ? <CloseIcon/> : <AddIcon/>}
      </button>
    </div>
  );
};

export {COLORS};
